import { FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { AiButton } from '@/components/ui/ai-button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useSchemaIntakeForm } from '@/features/schema-agent/hooks/use-schema-intake-form'
import {
  FOCUS_DOMAINS,
  intakeErrors,
  MAX_FOCUS_DOMAINS,
  MAX_REFERENCE_FILES,
  QUESTION_TEMPLATES,
} from '@/features/schema-agent/intake'
import type { Document } from '@/services/api/cases'
import type { SchemaSessionStartBody } from '@/services/api/schemaSessions'

export interface SchemaAgentKickoffProps {
  documents: Document[]
  context: { caseId: string; initial?: SchemaSessionStartBody; submitLabel?: string }
  onStart: (intake: SchemaSessionStartBody) => void
  isPending: boolean
}

type IntakeForm = ReturnType<typeof useSchemaIntakeForm>

function toggleValue<T extends string>(values: T[], value: T): T[] {
  return values.includes(value) ? values.filter(item => item !== value) : [...values, value]
}

export function SchemaAgentKickoff({
  documents,
  context,
  onStart,
  isPending,
}: SchemaAgentKickoffProps) {
  const { t } = useTranslation()
  const form = useSchemaIntakeForm(context)
  const allErrors = intakeErrors(form.buildIntake())
  // The reference-file section explains the selection rules in place.
  const errors = allErrors.filter(error => error !== 'documents' && error !== 'goalRequired')
  const invalidSelection = form.selected.some(id => !documents.some(doc => doc.id === id))
  const canStart = !isPending && !invalidSelection && allErrors.length === 0

  return (
    <div className="space-y-8 px-2 py-6 sm:px-4">
      <div className="space-y-2">
        <h2 className="text-lg font-semibold text-foreground">{t('schemaAgent.kickoff.title')}</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          {t('schemaAgent.kickoff.description')}
        </p>
      </div>
      <InvestigationGoal form={form} />
      <InvestigationQuestions form={form} />
      <ReferenceFiles documents={documents} form={form} />
      <div className="space-y-3 border-t border-border pt-5">
        {errors.length > 0 && (
          <ul role="alert" className="space-y-1 text-sm text-destructive">
            {errors.map(error => (
              <li key={error}>{t(`schemaAgent.validation.${error}`)}</li>
            ))}
          </ul>
        )}
        <AiButton
          emphasis="solid"
          size="lg"
          onClick={() => {
            if (canStart) onStart(form.buildIntake())
          }}
          disabled={!canStart}
          loading={isPending}
          className="w-full"
        >
          {context.submitLabel ?? t('schemaAgent.kickoff.start')}
        </AiButton>
      </div>
    </div>
  )
}

function InvestigationGoal({ form }: { form: IntakeForm }) {
  const { t } = useTranslation()
  return (
    <section className="space-y-3">
      <div className="space-y-1.5">
        <Label htmlFor="schema-investigation-goal">{t('schemaAgent.kickoff.goal')}</Label>
        <p
          id="schema-investigation-goal-help"
          className="text-xs leading-relaxed text-muted-foreground"
        >
          {t('schemaAgent.kickoff.goalHelp')}
        </p>
      </div>
      <Textarea
        id="schema-investigation-goal"
        aria-describedby="schema-investigation-goal-help"
        value={form.investigationGoal}
        onChange={event => form.setInvestigationGoal(event.target.value)}
        placeholder={t('schemaAgent.kickoff.goalPlaceholder')}
        className="min-h-28 resize-y"
        maxLength={4000}
      />
    </section>
  )
}

function InvestigationQuestions({ form }: { form: IntakeForm }) {
  const { t } = useTranslation()
  return (
    <section className="space-y-4">
      <div className="space-y-1.5">
        <h3 className="text-sm font-medium">{t('schemaAgent.kickoff.questions')}</h3>
        <p id="schema-questions-help" className="text-xs leading-relaxed text-muted-foreground">
          {t('schemaAgent.kickoff.questionsHelp')}
        </p>
      </div>
      <div className="space-y-1">
        {QUESTION_TEMPLATES.map(question => (
          <label
            key={question}
            className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-3 text-sm text-foreground hover:bg-muted/60 has-[[data-state=checked]]:bg-muted/60"
          >
            <Checkbox
              checked={form.questions.includes(question)}
              onCheckedChange={() => form.setQuestions(current => toggleValue(current, question))}
            />
            {t(`schemaAgent.questions.${question}`)}
          </label>
        ))}
      </div>
      <div className="space-y-2">
        <Label htmlFor="schema-custom-questions">{t('schemaAgent.kickoff.customQuestions')}</Label>
        <Textarea
          id="schema-custom-questions"
          aria-describedby="schema-questions-help"
          value={form.customQuestions}
          onChange={event => form.setCustomQuestions(event.target.value)}
          placeholder={t('schemaAgent.kickoff.customQuestionsPlaceholder')}
          className="min-h-24 resize-y"
          maxLength={4000}
        />
      </div>
    </section>
  )
}

function ReferenceFileRow({ document, form }: { document: Document; form: IntakeForm }) {
  const checked = form.selected.includes(document.id)
  return (
    <label className="flex cursor-pointer items-center gap-3 rounded px-2 py-2 hover:bg-muted/60">
      <Checkbox
        checked={checked}
        disabled={!checked && form.selected.length >= MAX_REFERENCE_FILES}
        onCheckedChange={() => form.setSelected(current => toggleValue(current, document.id))}
      />
      <FileText className="size-4 shrink-0 text-muted-foreground" />
      <span className="min-w-0 truncate text-sm text-foreground">{document.filename}</span>
    </label>
  )
}

function ReferenceFiles({ documents, form }: { documents: Document[]; form: IntakeForm }) {
  const { t } = useTranslation()
  return (
    <section className="space-y-3 border-t border-border pt-6">
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-medium">{t('schemaAgent.kickoff.documents')}</h3>
          <span className="text-xs tabular-nums text-muted-foreground">
            {form.selected.length} / {MAX_REFERENCE_FILES}
          </span>
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {t('schemaAgent.kickoff.documentsHelp')}
        </p>
      </div>
      {documents.length === 0 ? (
        <p className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
          {t('schemaAgent.kickoff.noDocuments')}
        </p>
      ) : (
        <div className="max-h-52 space-y-1 overflow-y-auto rounded-md border border-border p-2">
          {documents.map(document => (
            <ReferenceFileRow key={document.id} document={document} form={form} />
          ))}
        </div>
      )}
      {form.selected.length === 0 && (
        <>
          <WithoutFilesNotice goalMissing={!form.investigationGoal.trim()} />
          <FocusAreas form={form} />
        </>
      )}
    </section>
  )
}

/** Without files the analyst says what the schema covers beyond pole-basic: up to three catalogue areas. */
function FocusAreas({ form }: { form: IntakeForm }) {
  const { t } = useTranslation()
  const full = form.focusDomains.length >= MAX_FOCUS_DOMAINS
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-sm font-medium">{t('schemaAgent.kickoff.focus')}</h3>
          <span className="text-xs tabular-nums text-muted-foreground">
            {form.focusDomains.length} / {MAX_FOCUS_DOMAINS}
          </span>
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">
          {t('schemaAgent.kickoff.focusHelp')}
        </p>
      </div>
      <div className="space-y-1">
        {FOCUS_DOMAINS.map(domain => {
          const checked = form.focusDomains.includes(domain)
          return (
            <label
              key={domain}
              className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-sm text-foreground hover:bg-muted/60 has-[[data-state=checked]]:bg-muted/60 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60"
            >
              <Checkbox
                checked={checked}
                disabled={!checked && full}
                onCheckedChange={() =>
                  form.setFocusDomains(current => toggleValue(current, domain))
                }
              />
              {t(`schemaAgent.focus.${domain}`)}
            </label>
          )
        })}
      </div>
    </div>
  )
}

function WithoutFilesNotice({ goalMissing }: { goalMissing: boolean }) {
  const { t } = useTranslation()
  return (
    <div className="space-y-1.5 rounded-md bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
      <p>{t('schemaAgent.kickoff.withoutFiles')}</p>
      {goalMissing && (
        <p className="font-medium text-foreground">{t('schemaAgent.kickoff.withoutFilesGoal')}</p>
      )}
    </div>
  )
}
